/**
 * @file    RollupProgramModel.ts
 * @scope   SHARED
 * @summary Domain model for a contributor program of a cross-program rollup.
 * @consumes FieldFilter
 * @produces RollupProgramModel, createRollupProgramId, isSameRollupProgram
 * @sideEffects None (pure).
 * @notes Persisted as one `QueryCollections` entry carrying ONLY `DataCollectionGuid` + `OriginId`
 *        (docs/guardrails.md, "Cross-Program Rollup"). `WorkspaceGuid` is added by the FrontStack.
 *        Contributors are symmetric peers: never attach widget-level settings to one of them.
 *        TODO(pending): `questionGuid` / `questionLabel` / `filters` are DRAWER-ONLY and never
 *        persisted: the contract has no per-contributor question or filter (the metric is the
 *        widget-level `CustomCalculations`; filters are the widget-level `PartitionFilter`).
 */
import { FieldFilter } from 'models/Enterprise/FieldsManagerModel';

/** A contributor program. Order is meaningful (preserved through to the response). */
export interface RollupProgramModel {
  /** Stable client id (keys, drag-and-drop). Drawer-only; never persisted. */
  id: string;
  dataCollectionGuid: string;
  originId: string;
  /** UI-only label; never persisted. */
  displayName?: string;
  /** PENDING (drawer-only): question picked for this program. */
  questionGuid?: string;
  /** UI-only label; never persisted. */
  questionLabel?: string;
  /** PENDING (drawer-only): advanced filters of this program. */
  filters?: FieldFilter[];
}

/** @returns A new client-side id for a program row. */
export const createRollupProgramId = (): string =>
  `program-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** Programs are identified by their data collection (one contributor per collection). */
export const isSameRollupProgram = (a: RollupProgramModel, b: RollupProgramModel): boolean =>
  a.dataCollectionGuid === b.dataCollectionGuid;
